import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Skeleton } from 'components/ui/skeleton';
import { EnforcementSwitchCard } from 'features/usage-config/components/enforcement-switch-card';
import { ModelRestrictionCard } from 'features/usage-config/components/model-restriction-card';
import { SettlementCard } from 'features/usage-config/components/settlement-card';
import { UsageMetersCard } from 'features/usage-config/components/usage-meters-card';
import { UsageScopeList } from 'features/usage-config/components/usage-scope-list';
import {
  buildOrganizationPayload,
  draftFromConfig,
} from 'features/usage-config/components/usage-config-draft';
import { useUsageConfigDraft } from 'features/usage-config/components/use-usage-config-draft';
import {
  DEFAULT_ORGANIZATION,
  USAGE_LIMITS_I18N,
} from 'features/usage-config/constants/usage-meters';
import { useDebounce } from 'hooks/use-debounce';
import { notifyFailed, notifySaved } from 'features/usage-config/components/usage-toasts';
import {
  useConfiguredOrganizations,
  useOrganizationEnforcement,
  useSaveOrganizationEnforcement,
  useUsageEnforcement,
} from 'features/usage-config/hooks/use-usage-enforcement';
import { useOrganizations } from 'features/organization/hooks/use-organization';

const T = USAGE_LIMITS_I18N;
const PAGE_SIZE = 50;

export const OrganizationUsageConfig = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const [selectedId, setSelectedId] = useState('');

  // The list comes back whole; the name filter below narrows it here.
  // Every organization in the tenant: this view is a platform admin's.
  const { data: organizations, isLoading: isListLoading } = useOrganizations({
    page: 0,
    pageSize: PAGE_SIZE,
    all: true,
  });
  const { data: configuredIds } = useConfiguredOrganizations();
  const { data: tenantConfig } = useUsageEnforcement();
  const { data: config, isLoading, isError } = useOrganizationEnforcement(selectedId);
  const save = useSaveOrganizationEnforcement(selectedId);
  const { draft, setDraft, overrides, touch, follow, discard, isDirty } =
    useUsageConfigDraft(config);

  const all = useMemo(
    () => [
      { itemId: DEFAULT_ORGANIZATION.itemId, name: t(DEFAULT_ORGANIZATION.name) },
      ...(organizations?.data ?? [])
        .filter((org) => org.itemId !== DEFAULT_ORGANIZATION.itemId)
        .map((org) => ({ itemId: org.itemId, name: org.name })),
    ],
    [organizations, t]
  );
  const rows = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    return term ? all.filter((org) => org.name.toLowerCase().includes(term)) : all;
  }, [all, debouncedSearch]);
  const configured = useMemo(() => new Set(configuredIds ?? []), [configuredIds]);

  useEffect(() => {
    if (!selectedId && rows.length) setSelectedId(rows[0].itemId);
  }, [rows, selectedId]);

  const selected = rows.find((org) => org.itemId === selectedId);
  const isPending = save.isPending;
  const dimmed = Boolean(draft && (draft.isBlocked || !draft.enforcementEnabled));

  const submit = () => {
    if (!draft) return;
    save.mutate(
      buildOrganizationPayload(
        draft,
        overrides,
        tenantConfig ? draftFromConfig(tenantConfig) : undefined
      ),
      {
        onSuccess: () => notifySaved(t(T.SAVED)),
        onError: (error) => notifyFailed(t(T.SAVE_FAILED), error),
      }
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 lg:flex-row">
      <UsageScopeList
        items={rows.map((org) => ({
          id: org.itemId,
          name: org.name,
          isCustom: configured.has(org.itemId),
        }))}
        total={all.length}
        selectedId={selectedId}
        onSelect={setSelectedId}
        search={search}
        onSearch={setSearch}
        searchPlaceholder={t(T.ORGANIZATION_SEARCH)}
        inheritedLabel={t(T.CHIP_INHERITED_TENANT)}
        isLoading={isListLoading}
      />

      <div className="min-h-0 min-w-0 flex-1 overflow-y-auto rounded-sm border bg-card p-4">
        {!selectedId && <p className="text-sm text-muted-foreground">{t(T.SELECT_TO_CONFIGURE)}</p>}

        {selectedId && isLoading && <Skeleton className="h-40 w-full" />}

        {selectedId && isError && (
          <p className="rounded-sm bg-muted px-3 py-2 text-sm text-destructive">{t(T.LOAD_FAILED)}</p>
        )}

        {selectedId && !isLoading && config && draft && (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 border-b pb-4 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-semibold">{selected?.name ?? selectedId}</h2>
                  <Badge variant={config.is_saved ? 'secondary' : 'outline'}>
                    {config.is_saved ? t(T.CHIP_CUSTOM) : t(T.CHIP_INHERITED_TENANT)}
                  </Badge>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{selectedId}</p>
              </div>
            </div>

            <p className="max-w-prose text-xs text-muted-foreground">
              {t(T.ORGANIZATION_INHERIT_NOTE)}
            </p>

            <EnforcementSwitchCard
              description={t(T.ORGANIZATION_DESCRIPTION)}
              enforcing={draft.enforcementEnabled}
              onEnforcingChange={(enforcementEnabled) => {
                touch('enforcement_enabled');
                setDraft((current) => (current ? { ...current, enforcementEnabled } : current));
              }}
              enforcementInherited={!overrides.has('enforcement_enabled')}
              onFollowEnforcement={() =>
                tenantConfig && follow('enforcement_enabled', tenantConfig)
              }
              blocked={{
                value: draft.isBlocked,
                inherited: !overrides.has('is_blocked'),
                onChange: (isBlocked) => {
                  touch('is_blocked');
                  setDraft((current) => (current ? { ...current, isBlocked } : current));
                },
                onFollowTenant: () => tenantConfig && follow('is_blocked', tenantConfig),
              }}
              showOffNote={!draft.enforcementEnabled}
              readOnly={false}
            />

            <UsageMetersCard
              meters={draft.meters}
              enforcementEnabled={draft.enforcementEnabled}
              blocked={draft.isBlocked}
              onMeterChange={(meterKey, enabled) => {
                touch('meter_keys');
                setDraft((current) =>
                  current
                    ? {
                        ...current,
                        meters: {
                          ...current.meters,
                          [meterKey]: { ...current.meters[meterKey], enabled },
                        },
                      }
                    : current
                );
              }}
              hint={t(T.ORGANIZATION_SETUP_HINT)}
              dimmed={dimmed}
              inherited={!overrides.has('meter_keys')}
              onFollowTenant={() => tenantConfig && follow('meter_keys', tenantConfig)}
            />

            <ModelRestrictionCard
              restriction={draft.modelRestriction}
              onRestrictionChange={(modelRestriction) => {
                touch('model_restriction');
                setDraft((current) => (current ? { ...current, modelRestriction } : current));
              }}
              inherited={!overrides.has('model_restriction')}
              onFollowTenant={() => tenantConfig && follow('model_restriction', tenantConfig)}
            />

            <SettlementCard
              settlement={draft.settlement}
              onSettlementChange={(settlement) => {
                touch('settlement');
                setDraft((current) => (current ? { ...current, settlement } : current));
              }}
              dimmed={dimmed}
              settlementInherited={!overrides.has('settlement')}
              onFollowSettlement={() => tenantConfig && follow('settlement', tenantConfig)}
            />

            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={discard} disabled={!isDirty || isPending}>
                {t(T.DISCARD)}
              </Button>
              <Button onClick={submit} disabled={!isDirty || isPending}>
                {t(T.SAVE)}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
